import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// Component for each principle item with scroll-linked scaling
const ScrollLinkedPrincipleItem: React.FC<{
  reason: { number: string; title: string; description: string };
  index: number;
  isMobile: boolean;
}> = ({ reason, index, isMobile }) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isAtPeak, setIsAtPeak] = useState(false);
  
  // Track scroll progress for this specific item
  // Desktop: Add offset based on index for sequential appearance
  const desktopOffset = index % 3; // 0, 1, 2 for columns
  const { scrollYProgress } = useScroll({
    target: itemRef,
    offset: isMobile 
      ? ["start 100%", "end -5%"] // Mobile: Enter from bottom, exit closer to top
      : [`start ${100 - desktopOffset * 3}%`, `end ${15 - desktopOffset * 3}%`], // Desktop: Stay longer, exit closer to top
    layoutEffect: false // Use regular effect for faster updates
  });
  
  // Transform scroll progress to scale values with easing
  // Mobile & Desktop: Scale up from 0-15%, hold 15-85%, scale down 85-100%
  // Adding more keyframes for smoother easing
  const scale = useTransform(
    scrollYProgress,
    [0, 0.03, 0.06, 0.09, 0.12, 0.15, 0.85, 0.88, 0.91, 0.94, 0.97, 1],
    [0, 0.2, 0.45, 0.7, 0.9, 1, 1, 0.9, 0.7, 0.45, 0.2, 0], // Smooth ease in and out
    { clamp: true }
  );
  
  // Opacity based on scroll - with smooth easing
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.02, 0.05, 0.08, 0.12, 0.88, 0.92, 0.95, 0.98, 1],
    [0, 0.2, 0.5, 0.8, 1, 1, 0.8, 0.5, 0.2, 0],
    { clamp: true }
  );
  
  // Y position transform for parallax effect - with easing
  const y = useTransform(
    scrollYProgress,
    [0, 0.05, 0.1, 0.15, 0.85, 0.9, 0.95, 1],
    isMobile ? [40, 20, 10, 0, 0, -10, -20, -40] : [60, 30, 10, 0, 0, -10, -30, -60],
    { clamp: true }
  );
  
  // Separate transforms for title and description with smooth easing
  const titleScale = useTransform(
    scrollYProgress,
    [0, 0.03, 0.06, 0.09, 0.12, 0.88, 0.91, 0.94, 0.97, 1],
    [0, 0.3, 0.6, 0.85, 1, 1, 0.85, 0.6, 0.3, 0],
    { clamp: true }
  );
  
  // Description follows slightly after title with smooth easing
  const descriptionScale = useTransform(
    scrollYProgress,
    [0, 0.05, 0.08, 0.11, 0.14, 0.86, 0.89, 0.92, 0.95, 1],
    [0, 0.3, 0.6, 0.85, 1, 1, 0.85, 0.6, 0.3, 0],
    { clamp: true }
  );
  
  // Vertical movement for description with smooth easing
  const descriptionY = useTransform(
    scrollYProgress,
    [0, 0.05, 0.1, 0.15, 0.85, 0.9, 0.95, 1],
    isMobile ? [20, 10, 5, 0, 0, -5, -10, -20] : [30, 15, 5, 0, 0, -5, -15, -30],
    { clamp: true }
  );
  
  
  // Track when scale is at peak for mobile shiny animation and padding reduction
  useEffect(() => {
    if (!isMobile) return;
    
    const unsubscribe = scrollYProgress.on('change', (value) => {
      // Trigger shiny animation when at peak (much longer range now)
      const isNearPeak = value >= 0.4 && value <= 0.7;
      setIsAtPeak(isNearPeak);
    });
    
    return () => unsubscribe();
  }, [scrollYProgress, isMobile]);

  // Keep consistent padding to prevent horizontal shifts
  const mobilePaddingValue = '1rem';

  return (
    <motion.div
      ref={itemRef}
      className="group"
      style={{
        scale,
        opacity,
        y,
        transformOrigin: "center center",
        willChange: isMobile ? "opacity" : "transform, opacity",
        paddingLeft: isMobile ? mobilePaddingValue : undefined,
        paddingRight: isMobile ? mobilePaddingValue : undefined,
      }}
      transition={{ type: "tween", ease: "easeOut", duration: 0.3 }} // Smoother transition
    >
      <div className="relative">
        <motion.div 
          className="mb-2"
          style={{
            scale: titleScale,
            transformOrigin: isMobile ? "center center" : "left center",
            willChange: "transform",
            transform: "translateZ(0)" // GPU acceleration
          }}
        >
          {!isMobile ? (
            // Desktop version - CSS handles hover animation
            <div className="desktop-shiny-text inline-block">
              <span className="text-4xl font-display font-bold mr-4">{reason.number}</span>
              <span className="text-xl font-bold">{reason.title}</span>
            </div>
          ) : (
            // Mobile version - inline styles for dynamic animation
            <div 
              className={`inline-block ${isAtPeak ? 'mobile-shiny-active' : ''}`}
              style={{
                backgroundImage: isAtPeak
                  ? 'linear-gradient(90deg, #000000 0%, #000000 30%, #00ffff 50%, #000000 70%, #000000 100%)'
                  : 'none',
                backgroundSize: '200% 100%',
                backgroundPosition: '100% 0',
                WebkitBackgroundClip: isAtPeak ? 'text' : 'unset',
                backgroundClip: isAtPeak ? 'text' : 'unset',
                WebkitTextFillColor: isAtPeak ? 'transparent' : 'unset',
                animation: isAtPeak ? 'shine-sweep 2s linear infinite' : 'none'
              }}
            >
              <span className="text-4xl font-display font-bold mr-4">{reason.number}</span>
              <span className="text-xl font-bold">{reason.title}</span>
            </div>
          )}
        </motion.div>
        <motion.p 
          className={`text-base text-foreground/75 ${isMobile ? '' : 'ml-14'}`}
          style={{
            scale: descriptionScale,
            y: descriptionY,
            transformOrigin: isMobile ? "center center" : "left center",
            willChange: "transform",
            transform: "translateZ(0)" // GPU acceleration
          }}
        >
          {reason.description}
        </motion.p>
      </div>
    </motion.div>
  );
};

const WhyChooseUsSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Enhanced mobile detection with viewport changes
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  const reasons = [
    {
      number: '01',
      title: 'Expert Team',
      description: 'Seasoned developers with 7+ years of combined experience in web development and CCTV integration.'
    },
    {
      number: '02',
      title: 'Affordable Pricing',
      description: 'Competitive rates without compromising on quality. We believe great design should be accessible.'
    },
    {
      number: '03',
      title: 'Fast Delivery',
      description: 'Most projects completed within 2-4 weeks. We respect your time and business needs.'
    },
    {
      number: '04',
      title: 'Full Support',
      description: 'Comprehensive after-sales support. We are here whenever you need assistance or updates.'
    },
    {
      number: '05',
      title: 'Modern Tech',
      description: 'Using the latest technologies and best practices to ensure your website stays ahead.'
    },
    {
      number: '06',
      title: 'Custom Solutions',
      description: 'Every business is unique. We tailor our solutions to match your specific requirements.'
    }
  ];

  return (
    <section id="why-us" className="relative overflow-x-hidden">
      {/* Background Layer - Secondary color with grid */}
      <div className="absolute inset-0 bg-secondary">
        <div className="hero-grid" aria-hidden="true" />
        <div className="section-grain" aria-hidden="true" />
      </div>
      
      {/* Main Content Layer - Transparent background */}
      <div className="relative py-24 md:py-32">
        <div className="container relative mx-auto px-4 sm:px-4 lg:px-6">
        <div className="mb-16">
          <div className="flex items-center lg:hidden mb-3">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-2">[ 03 ]</span>
            <div className="w-20 border-t-2 border-foreground" />
          </div>
          <div className="hidden lg:flex flex-row items-baseline">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-6">[ 03 ]</span>
            <div>
              <div className="w-20 border-t-2 border-foreground mb-4"></div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Principles</h2>
            </div>
          </div>
          <div className="lg:hidden">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Principles</h2>
          </div>
        </div>

        <div 
          ref={sectionRef}
          className={`grid grid-cols-1 ${isMobile ? 'gap-12' : 'md:grid-cols-2 lg:grid-cols-3 gap-8'} mb-16 max-w-6xl ${isMobile ? 'mx-auto' : 'ml-auto'}`}
        >
          {reasons.map((reason, index) => (
            <ScrollLinkedPrincipleItem
              key={index}
              reason={reason}
              index={index}
              isMobile={isMobile}
            />
          ))}
        </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
